import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, Copy, Globe, Rocket } from 'lucide-react';
import {
  ApiError,
  createOrg,
  createOrgProject,
  qk,
  sdkScriptUrl,
  type OrgSummary,
  type Project,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Card from '../components/Card.js';
import { useOrgContext } from '../components/Layout.js';

/**
 * §43.2/4 — İLK GİRİŞ SİHİRBAZI: org adı → org kur → proje adı → proje kur → SDK
 * snippet'i.
 *
 * "Sihirbaz atlanamaz; org'suz kullanıcının gidebileceği başka sayfa yoktur."
 * Atlanamazlık BU SAYFADA DEĞİL `Layout`'ta kurulur (org'suzsa her yol buraya
 * yönlenir) — kilidi sayfanın kendi içine koymak, kullanıcının adres çubuğuna
 * `/bulgular` yazmasıyla delinirdi.
 *
 * Sihirbaz route guard'ın İÇİNDEDİR: org kurmak oturum gerektirir (§42.5 servis
 * belirtecini bile reddeder — sahipsiz org olmaz).
 *
 * Adım DEĞİŞKENİ YOKTUR: adım, VAR OLAN gerçeklerden türetilir (org var mı, proje
 * yaratıldı mı). Ayrı bir `step` sayacı, sunucu durumundan sapabilir ve kullanıcı
 * "2. adımda" görünürken aslında org'u olmayabilirdi.
 */

/** §42.5 org/proje uçlarının hata sözleşmesi — kod + durumdan okunur, metinden değil. */
function orgErrorText(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.status === 409) {
      /*
       * §42.3 — slug addan TÜRETİLİR ve çakışma sessizce çözülmez (`-2` eklenmez).
       * Kullanıcıya "başka bir ad" demek yeterli: kısa adı elle vermek bu ekranın
       * işi değildir, org ayarlarından değiştirilir.
       */
      return 'Bu ada karşılık gelen kısa ad kullanımda — biraz farklı bir ad deneyin.';
    }
    if (error.status === 0) return 'Sunucuya ulaşılamadı — bağlantınızı kontrol edin.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/** Adım başlığı — hangi adımda olduğumuz metinle de söylenir (renk tek taşıyıcı değil). */
function StepBadge({ index, active, done }: { index: number; active: boolean; done: boolean }) {
  const tone = done
    ? 'bg-brand text-white'
    : active
      ? 'bg-[#fff4ec] text-brand'
      : 'bg-[#f3f4f6] text-[#9ca3af]';
  return (
    <span
      aria-hidden="true"
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${tone}`}
    >
      {done ? <Check className="h-3.5 w-3.5" /> : index}
    </span>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgs, orgId, setOrgId } = useOrgContext();

  const [orgName, setOrgName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [createdOrg, setCreatedOrg] = useState<OrgSummary | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [copied, setCopied] = useState<'idle' | 'ok' | 'hata'>('idle');

  /*
   * Sihirbazın üzerinde çalıştığı org: bu oturumda KURULAN ya da (kullanıcı buraya
   * org'u varken geldiyse) listedeki seçili org. İkincisi kasıtlıdır: org'u olup
   * projesi olmayan kullanıcıya "org adı" sormak İKİNCİ bir org yaratırdı.
   *
   * Bağlamdaki `orgId` LİSTEYLE DOĞRULANIR: değeri localStorage'dan gelir (§42.5) ve
   * aynı tarayıcıda başka bir hesabın org'unu taşıyor olabilir. Doğrulamasaydık
   * org'suz kullanıcı, yabancı bir org'a proje açmaya çalışıp 403 alırdı.
   */
  const listOrgId = orgs.some((o) => o.id === orgId) ? orgId : (orgs[0]?.id ?? null);
  const activeOrgId = createdOrg?.id ?? listOrgId;
  const activeOrgName =
    createdOrg?.name ?? orgs.find((o) => o.id === activeOrgId)?.name ?? activeOrgId;

  const orgMut = useMutation({
    mutationFn: () => createOrg({ name: orgName.trim() }),
    onSuccess: async (data) => {
      /*
       * Yerel durum ÖNCE yazılır: `orgs` sorgusu tazelenene kadar liste hâlâ BOŞTUR
       * ve yalnız listeye baksaydık kullanıcı, az önce kurduğu org'un formunu bir kez
       * daha görüp ikinci bir org kurabilirdi.
       */
      setCreatedOrg(data.org);
      // Seçiciyi de yeni org'a çevir: bir sonraki adımın proje listesi ona bağlıdır.
      setOrgId(data.org.id);
      // `Layout`'un org sorgusu tazelenmezse "org yok" sanılır ve yönlendirme sürerdi.
      await queryClient.invalidateQueries({ queryKey: qk.orgs() });
    },
  });

  const projectMut = useMutation({
    mutationFn: () => createOrgProject(activeOrgId as string, { name: projectName.trim() }),
    onSuccess: async (data) => {
      /*
       * `api_key` YALNIZ bu yanıtta gelir (§42.5: liste ucunda ayıklanır), bu yüzden
       * bileşen durumunda TUTULUR — yeniden sorgulanamaz. Kullanıcı sayfayı
       * yenilerse anahtar bir daha gösterilemez; ekran bunu açıkça söyler.
       */
      setProject(data.project);
      await queryClient.invalidateQueries({ queryKey: qk.orgProjects(activeOrgId as string) });
    },
  });

  const orgDone = activeOrgId !== null;
  const projectDone = project !== null;
  const orgError = orgErrorText(orgMut.error);
  const projectError = orgErrorText(projectMut.error);

  const scriptUrl = sdkScriptUrl();
  const snippet =
    project === null
      ? ''
      : `<script src="${scriptUrl}" data-pid="${project.id}" async></script>`;

  async function copySnippet(): Promise<void> {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied('ok');
    } catch {
      // Pano izni yoksa (http, izin reddi) kullanıcı elle seçebilsin — sessiz kalınmaz.
      setCopied('hata');
    }
  }

  function submitOrg(event: FormEvent): void {
    event.preventDefault();
    if (orgName.trim() === '' || orgMut.isPending) return;
    orgMut.mutate();
  }

  function submitProject(event: FormEvent): void {
    event.preventDefault();
    if (projectName.trim() === '' || projectMut.isPending || activeOrgId === null) return;
    projectMut.mutate();
  }

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="kurulum-sayfasi">
      {/* başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-brand" aria-hidden="true" />
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Kuruluma başla</h1>
          <BilgiDugmesi testId="kurulum-bilgi">İlk kurulum sihirbazı: organizasyonunuzu ve ilk projenizi oluşturur, sitenize eklenecek SDK etiketini verir (§43.2). Etiket siteye eklendiğinde veri akmaya başlar ve panelin geri kalanı dolar.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Organizasyon → ilk proje → SDK etiketi (§43.2)
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-[720px] space-y-6 px-6 py-8">
        {/* 1. adım — organizasyon */}
        <Card
          title={
            <span className="flex items-center gap-2">
              <StepBadge index={1} active={!orgDone} done={orgDone} />
              <Building2 className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
              Organizasyon
            </span>
          }
          subtitle="Projeleriniz ve ekip üyeleriniz bu çatının altında toplanır. Kuran kişi org sahibidir."
        >
          {orgDone ? (
            <p data-testid="kurulum-org-hazir" className="text-[13px] text-[#374151]">
              Organizasyon hazır: <span className="font-medium">{activeOrgName}</span>
            </p>
          ) : (
            <form className="flex flex-wrap items-end gap-3" onSubmit={submitOrg}>
              <label className="flex min-w-[240px] flex-1 flex-col gap-1">
                <span className="text-[12px] text-[#6b7280]">Organizasyon adı</span>
                <input
                  data-testid="kurulum-org-ad"
                  type="text"
                  value={orgName}
                  autoFocus
                  onChange={(event) => setOrgName(event.target.value)}
                  placeholder="Örnek Ticaret A.Ş."
                  maxLength={120}
                  className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
                />
              </label>
              <button
                type="submit"
                data-testid="kurulum-org-gonder"
                className="btn-primary"
                disabled={orgName.trim() === '' || orgMut.isPending}
              >
                {orgMut.isPending ? 'Kuruluyor…' : 'Devam'}
              </button>
            </form>
          )}

          {orgError !== null ? (
            <p data-testid="kurulum-hata" role="alert" className="mt-2 text-[12px] text-red-600">
              {orgError}
            </p>
          ) : null}
        </Card>

        {/* 2. adım — ilk proje */}
        <Card
          title={
            <span className="flex items-center gap-2">
              <StepBadge index={2} active={orgDone && !projectDone} done={projectDone} />
              <Globe className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
              İlk proje
            </span>
          }
          subtitle="Ölçülecek her site/uygulama bir projedir. Proje kimliği (pid) ve anahtarı burada üretilir."
        >
          {!orgDone ? (
            <p className="text-[13px] text-[#9ca3af]">Önce organizasyonu kurun.</p>
          ) : projectDone ? (
            <p data-testid="kurulum-proje-hazir" className="text-[13px] text-[#374151]">
              Proje hazır: <span className="font-medium">{project.name}</span>{' '}
              <span className="font-mono text-[12px] text-[#6b7280]">({project.id})</span>
            </p>
          ) : (
            <form className="flex flex-wrap items-end gap-3" onSubmit={submitProject}>
              <label className="flex min-w-[240px] flex-1 flex-col gap-1">
                <span className="text-[12px] text-[#6b7280]">Proje adı</span>
                <input
                  data-testid="kurulum-proje-ad"
                  type="text"
                  value={projectName}
                  onChange={(event) => setProjectName(event.target.value)}
                  placeholder="Örnek Mağaza"
                  maxLength={120}
                  className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
                />
              </label>
              <button
                type="submit"
                data-testid="kurulum-proje-gonder"
                className="btn-primary"
                disabled={projectName.trim() === '' || projectMut.isPending}
              >
                {projectMut.isPending ? 'Açılıyor…' : 'Projeyi aç'}
              </button>
            </form>
          )}

          {projectError !== null ? (
            <p
              data-testid="kurulum-proje-hata"
              role="alert"
              className="mt-2 text-[12px] text-red-600"
            >
              {projectError}
            </p>
          ) : null}
        </Card>

        {/* 3. adım — SDK etiketi */}
        <Card
          title={
            <span className="flex items-center gap-2">
              <StepBadge index={3} active={projectDone} done={false} />
              SDK etiketi
            </span>
          }
          subtitle="Sitenizin <head> bölümüne bu tek satırı ekleyin; ilk olaylar birkaç dakikada panele düşer."
        >
          {project === null ? (
            <p className="text-[13px] text-[#9ca3af]">Proje açıldığında etiket burada belirir.</p>
          ) : (
            <div className="space-y-3">
              <pre
                data-testid="kurulum-snippet"
                className="max-w-full overflow-x-auto rounded-xl bg-[#171719] px-4 py-3 text-left font-mono text-[12px] text-[#e5e7eb]"
              >
                {snippet}
              </pre>

              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className="btn-ghost" onClick={() => void copySnippet()}>
                  <Copy className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
                  Etiketi kopyala
                </button>
                {copied === 'ok' ? (
                  <span className="text-[12px] text-emerald-600">Kopyalandı.</span>
                ) : copied === 'hata' ? (
                  <span className="text-[12px] text-[#9ca3af]">
                    Pano izni yok — metni elle seçip kopyalayın.
                  </span>
                ) : null}
              </div>

              {/*
                §43.2 proje anahtarı ETİKETİN İÇİNDE DEĞİL, AYRI gösterilir.

                NEDEN: tarayıcıya giden etiket herkese açıktır ve tracker-sdk yalnız
                `data-pid` okur (§9.3) — anahtarı oraya yazmak, sunucunun İSTEMEDİĞİ
                bir sırrı müşterinin HTML kaynağında yayımlamak olurdu. Anahtar
                sunucu-sunucu kullanımlar için saklanır ve YALNIZ bu yanıtta döner
                (§42.5), bu yüzden "şimdi kaydedin" uyarısıyla birlikte verilir.
              */}
              <div className="rounded-xl border border-[#e5e7eb] bg-[#fafafa] px-4 py-3">
                <p className="text-[12px] font-medium text-[#374151]">
                  Proje anahtarı — yalnız şimdi gösterilir
                </p>
                <p
                  data-testid="kurulum-api-key"
                  className="mt-1 break-all font-mono text-[12px] text-[#111827]"
                >
                  {project.api_key}
                </p>
                <p className="mt-1 text-[11px] text-[#9ca3af]">
                  Sayfayı yenilerseniz bir daha gösterilmez. Sunucu tarafı entegrasyonlar
                  için güvenli bir yere kaydedin; tarayıcıya YAPIŞTIRMAYIN.
                </p>
              </div>

              <button
                type="button"
                data-testid="kurulum-bitir"
                className="btn-primary"
                onClick={() => navigate('/', { replace: true })}
              >
                Panele git
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
